import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { storeName } from "../lib/labels";
import { api } from "../lib/api";

/** Small dropdown for a per-panel store override.
 *  Value: array of outlet ids (empty = all).
 *  Fires onChange with the new array whenever the selection flips. */
export default function LocalStorePicker({ value, onChange, label = "Stores", testId }) {
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/outlets").then((r) => setOutlets((r.data || []).filter((o) => o.is_retail)));
  }, []);

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const set = new Set(value || []);
  const isAll = set.size === 0 || set.size === outlets.length;
  const summary = isAll
    ? "All 4 stores"
    : value.length === 1
      ? storeName(outlets.find((o) => o.id === value[0])?.name || "")
      : `${value.length} stores`;

  function toggle(id) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    // If everything now selected, collapse to "all"
    if (next.size === outlets.length) onChange([]);
    else onChange(Array.from(next));
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={testId || "local-store-picker"}
        className="inline-flex items-center gap-1.5 bm-eyebrow border border-[#0a0a0a] px-2 py-1 hover:bg-[#0a0a0a]/5 bg-[#f5f2eb]"
      >
        <span className="text-neutral-500">{label}:</span>
        <span className="text-[#0a0a0a]">{summary}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-10 min-w-[220px] border border-[#0a0a0a] bg-[#f5f2eb] shadow-lg">
          <button
            onClick={() => { onChange([]); setOpen(false); }}
            className="w-full text-left px-3 py-2 bm-eyebrow border-b border-[#0a0a0a]/20 hover:bg-[#0a0a0a]/5 flex items-center justify-between"
            data-testid={`${testId || "local-store-picker"}-all`}
          >
            <span>All 4 stores</span>
            {isAll && <Check size={12} />}
          </button>
          {outlets.map((o) => {
            const selected = set.has(o.id);
            return (
              <button
                key={o.id}
                onClick={() => toggle(o.id)}
                data-testid={`${testId || "local-store-picker"}-${storeName(o.name)}`}
                className="w-full text-left px-3 py-2 bm-eyebrow border-b border-[#0a0a0a]/10 last:border-b-0 hover:bg-[#0a0a0a]/5 flex items-center justify-between"
              >
                <span>{storeName(o.name)}</span>
                {selected && <Check size={12} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
